import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../services/api';
import { differenceInDays } from 'date-fns';

interface Alert {
    title: string;
    message: string;
    project?: any;
    dress?: any;
    urgency?: string;
    daysUntilDeadline?: number;
}

interface DashboardPaginationParams {
    page?: number;
    pageSize?: number;
    upcomingLimit?: number;
    pastDueLimit?: number;
}

interface DashboardState {
    data: {
        summary: {
            totalClients: number;
            activeProjects: number;
            inProgressJobs: number;
            workStatusStats?: {
                readyForDelivery: number;
                delivered: number;
                pastDue: number;
                dueThisWeek: number;
            };
        };
        jobsByStage: { [key: string]: number };
        upcomingDeliveries: any[];
        alerts: Alert[];
    } | null;
    pagination: {
        page: number;
        pageSize: number;
        totalPages: number;
        totalAlerts: number;
        upcomingLimit: number;
        pastDueLimit: number;
    };
    isLoading: boolean;
    error: string | null;
}

const initialState: DashboardState = {
    data: null,
    pagination: {
        page: 1,
        pageSize: 5,
        totalPages: 1,
        totalAlerts: 0,
        upcomingLimit: 3,
        pastDueLimit: 5,
    },
    isLoading: false,
    error: null
};

// Calculate urgency based on days until deadline
const calculateUrgency = (daysUntilDeadline: number): string => {
    if (daysUntilDeadline < 0) return 'high'; // Past due
    if (daysUntilDeadline <= 2) return 'high';
    if (daysUntilDeadline <= 5) return 'medium';
    return 'normal';
};

// Calculate days until deadline
const calculateDaysUntilDeadline = (deliveryDate: string): number => {
    try {
        const today = new Date();
        const dueDate = new Date(deliveryDate);
        return differenceInDays(dueDate, today);
    } catch (error) {
        return 999; // Default to a large number if there's an error
    }
};

// Transform API response to match our expected structure
const transformDashboardData = (apiData: any, requestParams?: any) => {
    if (!apiData) return null;
    
    // Debug logging to understand API structure
    console.log('Dashboard API Response Structure:', {
        upcomingDeliveries: apiData.upcomingDeliveries,
        alerts: apiData.alerts,
        requestParams
    });

    // Transform upcomingDeliveries to match the expected structure in the DeliveryCalendar component
    const upcomingDeliveriesData = apiData.upcomingDeliveries?.data || [];
    const transformedDeliveries = upcomingDeliveriesData.map((delivery: any) => ({
        id: delivery.id,
        attributes: {
            name: delivery.name,
            deliveryDate: delivery.deliveryDate,
            stageName: delivery.stageName,
            dressCount: delivery.dressCount,
            client: {
                data: {
                    attributes: {
                        firstName: delivery.client?.firstName || '',
                        surname: delivery.client?.lastName || ''
                    }
                }
            }
        }
    }));

    // Transform jobsByStage from array to object
    const transformedJobsByStage = apiData.jobsByStage.reduce((acc: { [key: string]: number }, job: any) => {
        acc[job.stage] = job.count;
        return acc;
    }, {});

    // Transform alerts - reorganize to be dress-focused instead of project-focused
    const alertsArray: Alert[] = [];
    
    // Process past due deliveries
    const pastDueData = apiData.alerts?.pastDueDeliveries?.data || [];
    if (pastDueData.length > 0) {
        pastDueData.forEach((project: any) => {
            // If the project has dresses, create an alert for each dress
            if (project.dresses && project.dresses.length > 0) {
                project.dresses.forEach((dress: any) => {
                    const daysUntilDeadline = calculateDaysUntilDeadline(dress.deliveryDate);
                    alertsArray.push({
                        title: `Past Due: ${dress.name} (${project.name})`,
                        message: `Client: ${project.client?.firstName || ''} ${project.client?.lastName || ''}`,
                        project: project,
                        dress: dress,
                        urgency: 'high',
                        daysUntilDeadline: daysUntilDeadline
                    });
                });
            } else {
                // Fallback for projects without dresses
                alertsArray.push({
                    title: `Past Due: ${project.name}`,
                    message: `Client: ${project.client?.firstName || ''} ${project.client?.lastName || ''}`,
                    project: project,
                    urgency: 'high',
                    daysUntilDeadline: -1
                });
            }
        });
    }
    
    // Process upcoming deadlines - display alerts by dress rather than by project
    const upcomingDeadlinesData = apiData.alerts?.upcomingDeadlines?.data || [];
    if (upcomingDeadlinesData.length > 0) {
        upcomingDeadlinesData.forEach((project: any) => {
            if (project.dresses && project.dresses.length > 0) {
                project.dresses.forEach((dress: any) => {
                    const daysUntilDeadline = calculateDaysUntilDeadline(dress.deliveryDate);
                    const urgency = calculateUrgency(daysUntilDeadline);
                    
                    alertsArray.push({
                        title: `${urgency === 'high' ? 'Urgent' : 'Upcoming'}: ${dress.name} (${project.name})`,
                        message: `Due in ${daysUntilDeadline < 0 ? 'OVERDUE' : daysUntilDeadline + ' days'}`,
                        project: project,
                        dress: dress,
                        urgency: urgency,
                        daysUntilDeadline: daysUntilDeadline
                    });
                });
            } else {
                // Fallback for projects without dresses
                const daysUntilDeadline = calculateDaysUntilDeadline(project.deliveryDate);
                alertsArray.push({
                    title: `${project.urgency === 'high' ? 'Urgent' : 'Upcoming'}: ${project.name}`,
                    message: `Due in ${daysUntilDeadline < 0 ? 'OVERDUE' : daysUntilDeadline + ' days'}`,
                    project: project,
                    urgency: project.urgency,
                    daysUntilDeadline: daysUntilDeadline
                });
            }
        });
    }

    // Sort alerts by urgency (high to low) and then by days until deadline (ascending)
    alertsArray.sort((a, b) => {
        // First sort by urgency
        const urgencyOrder = { high: 0, medium: 1, normal: 2 };
        const urgencyA = a.urgency ? urgencyOrder[a.urgency as keyof typeof urgencyOrder] : 999;
        const urgencyB = b.urgency ? urgencyOrder[b.urgency as keyof typeof urgencyOrder] : 999;
        
        if (urgencyA !== urgencyB) {
            return urgencyA - urgencyB;
        }
        
        // Then sort by days until deadline
        return (a.daysUntilDeadline || 999) - (b.daysUntilDeadline || 999);
    });

    return {
        data: {
            summary: apiData.summary,
            jobsByStage: transformedJobsByStage,
            upcomingDeliveries: transformedDeliveries,
            alerts: alertsArray,
            updatedAt: apiData.updatedAt
        },
        pagination: {
            page: requestParams?.page || 1,
            pageSize: requestParams?.pageSize || 5,
            totalPages: Math.max(
                apiData.alerts?.pastDueDeliveries?.meta?.pagination?.pageCount || 1,
                apiData.alerts?.upcomingDeadlines?.meta?.pagination?.pageCount || 1
            ),
            totalAlerts: (apiData.alerts?.pastDueDeliveries?.meta?.pagination?.total || 0) + 
                        (apiData.alerts?.upcomingDeadlines?.meta?.pagination?.total || 0),
            upcomingLimit: requestParams?.upcomingLimit || 3,
            pastDueLimit: requestParams?.pastDueLimit || 5,
        }
    };
};

export const fetchDashboard = createAsyncThunk(
    'dashboard/fetchDashboard',
    async (params: DashboardPaginationParams = {}, { rejectWithValue }) => {
        try {
            const queryParams = new URLSearchParams();
            
            // Set default values and build query params
            const page = params.page || 1;
            const pageSize = params.pageSize || 5;
            const upcomingLimit = params.upcomingLimit || 3;
            const pastDueLimit = params.pastDueLimit || 5;
            
            queryParams.append('page', page.toString());
            queryParams.append('pageSize', pageSize.toString());
            queryParams.append('upcomingLimit', upcomingLimit.toString());
            queryParams.append('pastDueLimit', pastDueLimit.toString());
            
            const response = await api.get(`/api/dashboard?${queryParams.toString()}`);
            return { ...response.data, requestParams: { page, pageSize, upcomingLimit, pastDueLimit } };
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.error?.message || 'Failed to fetch dashboard data');
        }
    }
);

const dashboardSlice = createSlice({
    name: 'dashboard',
    initialState,
    reducers: {
        clearDashboardError: (state) => {
            state.error = null;
        },
        updatePaginationParams: (state, action) => {
            state.pagination = { ...state.pagination, ...action.payload };
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchDashboard.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchDashboard.fulfilled, (state, action) => {
                state.isLoading = false;
                const transformed = transformDashboardData(action.payload.data, action.payload.requestParams);
                if (transformed) {
                    state.data = transformed.data;
                    state.pagination = transformed.pagination;
                }
            })
            .addCase(fetchDashboard.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            });
    }
});

export const { clearDashboardError, updatePaginationParams } = dashboardSlice.actions;
export default dashboardSlice.reducer; 